import {
	Create, Edit,
	SimpleForm,
	TextInput,

	List,
	Datagrid,
	TextField,
	ReferenceArrayInput,
	AutocompleteArrayInput,
	FileInput,
	useRecordContext,
	useNotify,
	useRefresh,
	useDataProvider,
	Button,
	Toolbar,
	SaveButton,
	DeleteButton,
	useRedirect,
	Count,
	ReferenceManyCount
} from 'react-admin';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button as MuiButton } from '@mui/material';

import { enforceGroupId } from '../util';
import { DocumentFileField } from './Document/components';

// Statuses that indicate processing is in progress
const PROCESSING_STATUSES = ['pending', 'extracting', 'reindexing'];
const POLLING_INTERVAL = 7000; // 7 seconds

/**
 * Hook to poll for document status updates when documents are processing
 */
const useDocumentPolling = () => {
	const record = useRecordContext();
	const refresh = useRefresh();
	const intervalRef = useRef(null);

	useEffect(() => {
		// Check if any documents are in a processing state
		const hasProcessingDocuments = record?.documents?.some(
			doc => PROCESSING_STATUSES.includes(doc.status)
		);

		if (hasProcessingDocuments && !intervalRef.current) {
			// Start polling
			intervalRef.current = setInterval(() => {
				refresh();
			}, POLLING_INTERVAL);
		} else if (!hasProcessingDocuments && intervalRef.current) {
			// Stop polling when no documents are processing
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		// Cleanup on unmount
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [record?.documents, refresh]);
};

const DocumentInput = () => {
	return (
		<FileInput source="documents" multiple>
			<DocumentFileField />
		</FileInput>
	);
}

// Re-extract all documents in this topic
const ReExtractAllDocumentsButton = () => {
	const record = useRecordContext();
	const notify = useNotify();
	const refresh = useRefresh();
	const dataProvider = useDataProvider();

	const handleReExtractAll = async () => {
		if (!record?.documents || record.documents.length === 0) {
			notify('No documents to extract', { type: 'info' });
			return;
		}

		const documentIds = record.documents.map(doc => doc.id);

		try {
			notify('Re-extracting documents...', { type: 'info' });

			// Start the extraction process (no status change needed - extraction will handle it)
			const { data } = await dataProvider.refreshDocumentsContent({
				ids: documentIds,
				setReindexingStatus: false
			});

			if (data.success) {
				const successCount = data.results.filter(r => r.success).length;
				const failCount = data.results.length - successCount;

				let message = `${successCount} document(s) extracted successfully`;
				if (failCount > 0) {
					message += `, ${failCount} failed`;
				}

				notify(message, { type: successCount > 0 ? 'success' : 'warning' });
				refresh();
			} else {
				notify('Failed to extract content', { type: 'error' });
				refresh();
			}
		} catch (error) {
			notify(`Error: ${error.message}`, { type: 'error' });
			refresh();
		}
	};

	const documentCount = record?.documents?.length || 0;

	return (
		<Button
			label={`Re-extract All Documents (${documentCount})`}
			onClick={handleReExtractAll}
			variant="outlined"
			disabled={documentCount === 0}
			sx={{ marginLeft: 2 }}
		>
			<RefreshIcon />
		</Button>
	);
};

// Reindex topic button
const ReindexTopicButton = () => {
	const record = useRecordContext();
	const dataProvider = useDataProvider();
	const notify = useNotify();
	const refresh = useRefresh();
	const [loading, setLoading] = useState(false);

	const handleReindex = async () => {
		if (!record?.id) return;

		setLoading(true);
		try {
			const json = await dataProvider.reindexTopic( record.id );

			if (json.success) {
				notify(json.message || `${json.queued} documents queued for reindexing`, { type: 'success' });
				refresh();
			} else {
				notify(json.message || 'Reindexing failed', { type: 'error' });
			}
		} catch (error) {
			console.error('Reindex error:', error);
			notify(`Error: ${error.message}`, { type: 'error' });
		} finally {
			setLoading(false);
		}
	};

	const documentCount = record?.documents?.length || 0;

	return (
		<Button
			label={`Reindex All Documents (${documentCount})`}
			onClick={handleReindex}
			variant="outlined"
			disabled={documentCount === 0 || loading}
			sx={{ marginLeft: 2 }}
		>
			<CloudSyncIcon />
		</Button>
	);
};

// Custom delete button with library association check
const TopicDeleteButton = () => {
	const record = useRecordContext();
	const notify = useNotify();
	const redirect = useRedirect();
	const dataProvider = useDataProvider();
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [libraryInfo, setLibraryInfo] = useState(null);

	const handleConfirmedDelete = async () => {
		try {
			// Delete with confirmation query parameter
			await dataProvider.delete('topics', {
				id: record.id,
				previousData: record,
				meta: { confirm: true }
			});
			notify('Topic deleted successfully', { type: 'success' });
			setConfirmOpen(false);
			redirect('/topics');
		} catch (error) {
			notify(error.body?.message || 'Error deleting topic', { type: 'error' });
			setConfirmOpen(false);
		}
	};

	const handleCancel = () => {
		setConfirmOpen(false);
		setLibraryInfo(null);
	};

	return (
		<>
			<DeleteButton
				sx={{ marginLeft: 'auto' }}
				mutationOptions={{
					onError: (error) => {
						if (error.body?.requiresConfirmation) {
							setLibraryInfo(error.body);
							setConfirmOpen(true);
						}
					}
				}}
			/>
			<Dialog open={confirmOpen} onClose={handleCancel}>
				<DialogTitle>Confirm Topic Deletion</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{libraryInfo?.message}
					</DialogContentText>
					{libraryInfo?.libraries && libraryInfo.libraries.length > 0 && (
						<DialogContentText sx={{ mt: 2 }}>
							<strong>Associated Libraries:</strong>
							<ul>
								{libraryInfo.libraries.map(lib => (
									<li key={lib.id}>{lib.name}</li>
								))}
							</ul>
						</DialogContentText>
					)}
					<DialogContentText sx={{ mt: 2 }}>
						Are you sure you want to proceed?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<MuiButton onClick={handleCancel}>Cancel</MuiButton>
					<MuiButton onClick={handleConfirmedDelete} color="error" variant="contained">
						Delete Topic
					</MuiButton>
				</DialogActions>
			</Dialog>
		</>
	);
};

// Custom toolbar with re-extract and reindex buttons
const TopicEditToolbar = () => {
	const record = useRecordContext();

	// Enable polling when documents are processing
	useDocumentPolling();

	return (
		<Toolbar>
			<SaveButton />
			{record?.id && <ReExtractAllDocumentsButton />}
			{record?.id && <ReindexTopicButton />}
			<TopicDeleteButton />
		</Toolbar>
	);
};

const FormFields = (
	<SimpleForm>
		<TextInput source="name" />
		<TextInput source="description" multiline />
		<ReferenceArrayInput source="library_ids" reference="libraries">
			<AutocompleteArrayInput
				optionText="name"
				label="Libraries"
				helperText="Select libraries to associate with this topic"
			/>
		</ReferenceArrayInput>
		<DocumentInput />
	</SimpleForm>
);

const create = () => (
	<Create record={enforceGroupId()} mutationMode="pessimistic">
		{FormFields}
	</Create>
);

const edit = () => (
	<Edit record={enforceGroupId()} mutationMode="pessimistic">
		<SimpleForm toolbar={<TopicEditToolbar />}>
			<TextInput source="name" />
			<TextInput source="description" multiline />
			<ReferenceArrayInput source="library_ids" reference="libraries">
				<AutocompleteArrayInput
					optionText="name"
					label="Libraries"
					helperText="Select libraries to associate with this topic"
				/>
			</ReferenceArrayInput>
			<DocumentInput />
		</SimpleForm>
	</Edit>
);

const list = () => (
	<List>
		<Datagrid>
			<TextField source="name" />
			<ReferenceManyCount label="Documents" reference="documents" target="topic_id" />
		</Datagrid>
	</List>
);

export const Topic = {
	list,
	edit,
	create
};

export default Topic;